import React, { Component } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import api from '@/shared/api';
import config from '@/config';
import { CanCreate, CanDelete } from '@/shared/components/security';
import { showWhenAssignmentsEnabled } from '@/shared/components/security/HasAssignments';
import authService, { privileges, functionalities } from '@/shared/services/AuthService';
import YesNoModal from '@/shared/components/YesNoModal';
import handleHttpError from '@/shared/services/HandleHttpError';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import FormattedListValue, {
  listItemMsg,
  formatListValue,
} from '@/shared/components/FormattedListValue';

import EditableTextbox from '@/shared/components/editable/EditableTextbox';
import messages from './assignmentTemplateMessages';
import LoadingAnimation from '../../../shared/components/LoadingAnimation';
import Checkbox from '../../../shared/components/input/Checkbox';
import { AddIcon, DeleteIcon } from '../../../shared/components/Icons';

import AddAssignmentTemplateModal from './AddAssignmentTemplateModal';
import AddTaskTemplateModal from './AddTaskTemplateModal';
import EditableSelect from '../../../shared/components/editable/EditableSelect';

import TranslateListItemModal from '../../../shared/components/translations/TranslateListItemModal';
import EditAssignmentTemplateResourcesModal from './EditAssignmentTemplateResourcesModal';
import guid from '@/shared/utils/guid';
import DevelopmentOnly from '@/shared/components/security/DevelopmentOnly';

class AssignmentTemplateContainer extends Component {
  state = {
    templates: null,
    showAddModal: false,
    deleteTemplate: null,
    addTaskTemplate: null,
  };

  mapAssignmentTemplateTaskTemplate = x => {
    const { intl } = this.props;
    return {
      ...x,
      taskTemplateName: intl.formatMessage(
        listItemMsg('tasktemplate', x.taskTemplateId, x.taskTemplateName),
      ),
      taskPeriodicityName: x.taskPeriodicityId
        ? intl.formatMessage(
            listItemMsg('taskperiodicity', x.taskPeriodicityId, x.taskPeridodicityName),
          )
        : null,
    };
  };

  loadTemplates = () => {
    Promise.all([
      api.base.get(`${config.appUrls.api}/api/AssignmentTemplate/Get`).then(({ data }) => data),
      api.base.get(`${config.appUrls.tasks}/api/AssignmentTemplate`).then(({ data }) => data),
      api.base.get(`${config.appUrls.tasks}/api/TaskTemplates/Schedules`).then(({ data }) => data),
    ]).then(
      ([templates, assignmentTaskTemplates, allTaskTemplates]) => {
        const taskTemplates = assignmentTaskTemplates.map(this.mapAssignmentTemplateTaskTemplate);
        const { intl } = this.props;
        this.setState({
          templates: templates
            .map(x => ({
              ...x,
              showDetails: false,
              taskTemplates: taskTemplates
                .filter(t => t.assignmentTemplateId === x.id)
                .sort(dynamicSort('taskTemplateName')),
            }))
            .sort(dynamicSort('name')),
          taskTemplates: allTaskTemplates
            .map(({ id, name, periodicities = [] }) => ({
              id,
              label: intl.formatMessage(listItemMsg('tasktemplate', id, name)),
              periodicities: periodicities
                .map(p => ({
                  id: p.id,
                  label: intl.formatMessage(listItemMsg('taskperiodicity', p.id, p.name)),
                }))
                .sort(dynamicSort('label')),
            }))
            .sort(dynamicSort('label')),
        });
      },
      error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
      },
    );
  };

  componentDidMount() {
    this.loadTemplates();
  }

  appendTemplate = template => {
    if (!template || !template.id) {
      this.setState({ showAddModal: false });
      return;
    }
    const newTemplate = { ...template, showDetails: true, taskTemplates: [] };

    this.setState(({ templates }) => ({
      templates: [...templates, newTemplate].sort(dynamicSort('name')),
      showAddModal: false,
    }));
  };

  toggleDetails = template => {
    this.setState(({ templates }) => ({
      templates: templates.map(x => {
        if (x.id !== template.id) return x;
        return { ...x, showDetails: !x.showDetails };
      }),
    }));
  };

  applyTemplateUpdate = ({ data }) => {
    if (!data) return '';
    this.setState(({ templates }) => ({
      templates: templates.map(x => (x.id === data.id ? { ...x, ...data } : x)),
    }));
    return data;
  };

  updateTemplate = (template, changes) => {
    const model = { ...template, ...changes };
    return api.base
      .put(`${config.appUrls.api}/api/AssignmentTemplate`, model)
      .then(this.applyTemplateUpdate, error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
        return Promise.reject();
      });
  };

  updateMergeWith = (template, mergeWithId) =>
    api.base
      .put(`${config.appUrls.api}/api/AssignmentTemplate/MergeWith`, {
        id: template.id,
        mergeWithId,
      })
      .then(this.applyTemplateUpdate, error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
        return Promise.reject();
      });
  deleteTemplate = template => {
    if (!template || !template.id) {
      this.setState({
        deleteTemplate: null,
      });
      return;
    }
    return api.base
      .delete(`${config.appUrls.api}/api/AssignmentTemplate/Delete/${template.id}`)
      .then(
        () =>
          api.base
            .delete(`${config.appUrls.tasks}/api/AssignmentTemplate/Delete/${template.id}`)
            .then(() => {
              this.setState(({ templates }) => ({
                templates: templates.filter(x => x.id !== template.id),
                deleteTemplate: null,
              }));
            }),
        error => {
          const { intl } = this.props;
          handleHttpError(intl)(error);
          return Promise.reject();
        },
      );
  };
  deleteTaskTemplate = (templateId, taskTemplateId) => {
    if (!templateId || !taskTemplateId) return;
    return api.base
      .delete(
        `${config.appUrls.tasks}/api/AssignmentTemplate/Delete/${templateId}/${taskTemplateId}`,
      )
      .then(
        () => {
          this.setState(({ templates }) => ({
            templates: templates.map(x => {
              if (x.id !== templateId) return x;
              return {
                ...x,
                taskTemplates: x.taskTemplates.filter(t => t.taskTemplateId !== taskTemplateId),
              };
            }),
          }));
        },
        error => {
          const { intl } = this.props;
          handleHttpError(intl)(error);
        },
      );
  };

  showAddTaskTemplateModal = template => {
    const usedTemplates = (template.taskTemplates || []).map(x => x.taskTemplateId);
    this.setState(({ taskTemplates }) => ({
      addTaskTemplate: {
        assignmentTemplateId: template.id,
        taskTemplates: taskTemplates.filter(t => usedTemplates.indexOf(t.id) < 0),
      },
    }));
  };
  addTaskTemplate = taskTemplate => {
    if (!taskTemplate || !taskTemplate.assignmentTemplateId) {
      this.setState({ addTaskTemplate: null });
      return;
    }
    const newTemplate = this.mapAssignmentTemplateTaskTemplate(taskTemplate);

    this.setState(({ templates }) => ({
      addTaskTemplate: null,
      templates: templates.map(x => {
        if (x.id !== taskTemplate.assignmentTemplateId) return x;
        return {
          ...x,
          taskTemplates: [...x.taskTemplates, newTemplate].sort(dynamicSort('taskTemplateName')),
        };
      }),
    }));
  };
  updateTaskTemplate = (taskTemplate, changes) => {
    if (!taskTemplate || !taskTemplate.assignmentTemplateId) {
      return;
    }
    const model = { ...taskTemplate, ...changes };
    return api.base.put(`${config.appUrls.tasks}/api/AssignmentTemplate`, model).then(
      ({ data }) => {
        if (!data) return '';
        const updatedTaskTemplate = this.mapAssignmentTemplateTaskTemplate(data);

        this.setState(({ templates }) => ({
          templates: templates.map(x => {
            if (x.id !== updatedTaskTemplate.assignmentTemplateId) return x;
            return {
              ...x,
              taskTemplates: x.taskTemplates.map(t => {
                if (t.taskTemplateId !== updatedTaskTemplate.taskTemplateId) return t;
                return { ...t, ...updatedTaskTemplate };
              }),
            };
          }),
        }));
        return updatedTaskTemplate;
      },
      error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
        return Promise.reject();
      },
    );
  };
  openTranslateDialog = template => {
    const { saving } = this.state;
    if (saving) return;
    this.setState({
      translationModal: {
        list: 'assignmenttemplate',
        listId: template.id,
        fallback: template.name,
      },
    });
  };
  handleTranslationModalClosed = result => {
    if (!result) {
      // Cancelled
      this.setState({ translationModal: null });
      return;
    }
    const { listId, fallback = '' } = result;
    const { templates } = this.state;
    const template = templates.find(x => x.id === listId);
    if (template && template.name !== fallback) {
      this.updateTemplate(template, { name: fallback }).then(() => {
        this.setState({ translationModal: null });
      });
    } else {
      this.setState({ translationModal: null });
    }
  };

  render() {
    const {
      templates,
      showAddModal,
      deleteTemplate,
      addTaskTemplate,
      taskTemplates,
      translationModal,
      editBlockedResources,
    } = this.state;
    const { intl } = this.props;

    const canUpdate = authService.hasPermission({
      functionality: functionalities.Settings,
      privilege: privileges.Update,
    });
    const periodicities = (taskTemplates || []).reduce((o, e) => {
      // eslint-disable-next-line no-param-reassign
      o[e.id] = e.periodicities;
      return o;
    }, {});
    const mergeableTemplates = [
      { id: guid.empty, label: intl.formatMessage(messages.DefaultAssignment) },
      ...(templates || [])
        .filter(t => t.mergeWithAssignmentTemplateId === null)
        .map(t => ({
          id: t.id,
          label: formatListValue({ list: 'assignmenttemplate', id: t.id, fallback: t.name, intl }),
        })),
    ];
    return (
      <div id="AssignmentTemplateOverviewPage">
        <div className="panel">
          <div className="panel-heading">
            <h3 className="panel-title">
              <FormattedMessage {...messages.AssignmentTemplates} />
              <ZendeskHelp article="5434869059730" className="margin5L" />
            </h3>
            <div className="panel-buttons">
              <button
                type="button"
                className="btn btn-default"
                onClick={() =>
                  this.setState({ editBlockedResources: { assignmentTemplateId: guid.empty } })
                }
              >
                <i className="fa fa-cog" />{' '}
                <FormattedMessage {...messages.ConfigureAllowResourcesForDefaultAssignment} />
              </button>
              <CanCreate on="settings">
                <button
                  type="button"
                  className="btn btn-inverse"
                  onClick={() => this.setState({ showAddModal: true })}
                >
                  <i className="fa fa-plus" /> <FormattedMessage {...messages.Add} />
                </button>
              </CanCreate>
            </div>
          </div>
        </div>
        {!templates && <LoadingAnimation />}
        {templates && templates.length > 0 && (
          <table className="table table-grid table-hover">
            <thead>
              <tr>
                <th style={{ width: '50px' }} />
                <th>
                  <FormattedMessage {...messages.Code} />
                </th>
                <th>
                  <FormattedMessage {...messages.Name} />
                </th>
                <th className="text-center">
                  <FormattedMessage {...messages.AppliesToPerson} />
                </th>
                <th className="text-center">
                  <FormattedMessage {...messages.AppliesToSoleProprietor} />
                </th>
                <th className="text-center">
                  <FormattedMessage {...messages.AppliesToCompany} />
                </th>
                <th className="text-center">
                  <FormattedMessage {...messages.AllowMultiplePerRelation} />
                </th>
                <DevelopmentOnly>
                  <th>
                    <FormattedMessage {...messages.MergeWith} />
                  </th>
                </DevelopmentOnly>
                <th />
              </tr>
            </thead>
            {templates.map(template => (
              <tbody key={template.id} className="hover">
                <tr onDoubleClickCapture={() => this.toggleDetails(template)}>
                  <td className="clickable" onClick={() => this.toggleDetails(template)}>
                    {template.showDetails ? <FaCaretUp /> : <FaCaretDown />}
                  </td>
                  <td>
                    <EditableTextbox
                      value={template.code}
                      onChange={value => this.updateTemplate(template, { code: value })}
                      maxLength="15"
                      readOnly={!canUpdate}
                    />
                  </td>
                  <td>
                    {canUpdate ? (
                      <label
                        className="bold clickable"
                        onClick={e => {
                          e.stopPropagation();
                          this.openTranslateDialog(template);
                        }}
                      >
                        <FormattedListValue
                          list="assignmenttemplate"
                          id={template.id}
                          fallback={template.name}
                        />
                      </label>
                    ) : (
                      <FormattedListValue
                        list="assignmenttemplate"
                        id={template.id}
                        fallback={template.name}
                      />
                    )}
                  </td>
                  <td className="text-center">
                    <Checkbox
                      value={template.appliesToPerson}
                      disabled={!canUpdate}
                      onClick={checked =>
                        this.updateTemplate(template, { appliesToPerson: checked })
                      }
                    />
                  </td>
                  <td className="text-center">
                    <Checkbox
                      value={template.appliesToSoleProprietor}
                      disabled={!canUpdate}
                      onClick={checked =>
                        this.updateTemplate(template, { appliesToSoleProprietor: checked })
                      }
                    />
                  </td>
                  <td className="text-center">
                    <Checkbox
                      value={template.appliesToCompany}
                      disabled={!canUpdate}
                      onClick={checked =>
                        this.updateTemplate(template, { appliesToCompany: checked })
                      }
                    />
                  </td>
                  <td className="text-center">
                    <Checkbox
                      value={template.allowMultiple}
                      disabled={!canUpdate}
                      onClick={checked => this.updateTemplate(template, { allowMultiple: checked })}
                    />
                  </td>
                  <DevelopmentOnly>
                    <td>
                      <EditableSelect
                        value={template.mergeWithAssignmentTemplateId}
                        bindToId
                        required={false}
                        readOnly={!canUpdate}
                        options={mergeableTemplates.filter(o => o.id !== template.id)}
                        onChange={newId => this.updateMergeWith(template, newId)}
                        placeholder={intl.formatMessage(messages.DoNotMerge)}
                        displayValue={
                          template.mergeWithAssignmentTemplateId
                            ? undefined
                            : intl.formatMessage(messages.DoNotMerge)
                        }
                      />
                    </td>
                  </DevelopmentOnly>
                  <td className="text-right">
                    <button
                      type="button"
                      className="btn btn-default btn-xs btn-icon"
                      onClick={() =>
                        this.setState({
                          editBlockedResources: {
                            assignmentTemplateId: template.id,
                            assignmentTemplateName: template.name,
                          },
                        })
                      }
                      title={intl.formatMessage(messages.ConfigureAllowResources)}
                    >
                      <i className="fa fa-cog" />
                    </button>
                    <CanDelete on="settings">
                      <a
                        onClick={() => this.setState({ deleteTemplate: template })}
                        className="btn btn-xs btn-icon"
                      >
                        <DeleteIcon />
                      </a>
                    </CanDelete>
                  </td>
                </tr>
                {template.showDetails && (
                  <tr>
                    <td />
                    <td colSpan="7">
                      {template.taskTemplates && template.taskTemplates.length > 0 && (
                        <div className="subtable-wrapper">
                          <table className="table subtable">
                            <caption>
                              <FormattedMessage {...messages.TaskTemplates} />
                            </caption>
                            <thead>
                              <tr>
                                <th>
                                  <FormattedMessage {...messages.TaskTemplate} />
                                </th>
                                <th>
                                  <FormattedMessage {...messages.DefaultTaskPeriodicity} />
                                </th>
                                <th />
                              </tr>
                            </thead>
                            <tbody>
                              {template.taskTemplates.map(task => (
                                <tr key={task.taskTemplateId}>
                                  <td>{task.taskTemplateName}</td>
                                  <td>
                                    <EditableSelect
                                      value={task.taskPeriodicityId}
                                      bindToId
                                      readOnly={!canUpdate}
                                      options={periodicities[task.taskTemplateId] || []}
                                      onChange={newId =>
                                        this.updateTaskTemplate(task, {
                                          taskPeriodicityId: newId,
                                        })
                                      }
                                      placeholder={intl.formatMessage(messages.NoDefault)}
                                      displayValue={
                                        task.taskPeriodicityId ? null : (
                                          <i
                                            title={intl.formatMessage(
                                              messages.ChoosePeriodicityWhenCreatingAssignment,
                                            )}
                                          >
                                            <FormattedMessage {...messages.NoDefault} />
                                          </i>
                                        )
                                      }
                                    />
                                  </td>
                                  <td className="text-right">
                                    <CanDelete on="settings">
                                      <a
                                        onClick={() =>
                                          this.deleteTaskTemplate(template.id, task.taskTemplateId)
                                        }
                                      >
                                        <DeleteIcon />
                                      </a>
                                    </CanDelete>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      <CanCreate on="settings">
                        <div>
                          <button
                            type="button"
                            className="btn btn-inverse btn-xs"
                            onClick={() => this.showAddTaskTemplateModal(template)}
                          >
                            <i className="fa fa-plus" /> <FormattedMessage {...messages.Add} />
                          </button>
                        </div>
                      </CanCreate>
                    </td>
                  </tr>
                )}
              </tbody>
            ))}
          </table>
        )}
        {templates && !templates.length && (
          <p className="margin20B margin10T">
            <FormattedMessage {...messages.NoAssignmentTemplates} />
          </p>
        )}

        {showAddModal && (
          <AddAssignmentTemplateModal onClose={template => this.appendTemplate(template)} />
        )}
        {deleteTemplate !== null && (
          <YesNoModal
            intl={intl}
            show
            title={intl.formatMessage(messages.DeleteAssignmentTemplate)}
            message={intl.formatMessage(messages.DeleteAssignmentTemplateConfirmationMsg)}
            onNo={() =>
              this.setState(state => ({
                ...state,
                deleteTemplate: null,
              }))
            }
            onSave={() => this.deleteTemplate(deleteTemplate)}
          />
        )}
        {addTaskTemplate && (
          <AddTaskTemplateModal
            {...addTaskTemplate}
            onClose={taskTemplate => this.addTaskTemplate(taskTemplate)}
          />
        )}
        {translationModal && (
          <TranslateListItemModal
            {...translationModal}
            onClose={this.handleTranslationModalClosed}
            show={!!translationModal.listId}
            maxLength={100}
          />
        )}
        {editBlockedResources && (
          <EditAssignmentTemplateResourcesModal
            {...editBlockedResources}
            onClose={() => this.setState({ editBlockedResources: null })}
          />
        )}
      </div>
    );
  }
}
export default showWhenAssignmentsEnabled(injectIntl(AssignmentTemplateContainer));
